/**
 * Diagnostics drawer surfaced when the team clicks a PlatformHealthBadge.
 *
 * Shows: per-platform last attempt / last success / row count / error verbatim
 * + the resolved platformIds + a one-click "Retry pull". Designed so an
 * operator can self-serve "why isn't YouTube data flowing?" without paging
 * engineering — error messages are rendered verbatim from the integration.
 */

import React, { useEffect, useState } from 'react';
import type { CgBoostCampaign, CgBoostPullStatus, CgBoostPlatform } from '../../types/cgBoost';
import { PLATFORM_LABELS, refreshCampaign } from '../../services/cgBoostApi';
import { formatTimeAgo } from '../../utils/dateFormatters';
import { Modal, Button } from '../ui';
import PlatformHealthBadge from './PlatformHealthBadge';

interface Props {
  open: boolean;
  onClose: () => void;
  campaign: CgBoostCampaign;
  statuses: CgBoostPullStatus[];
  /** Called after a successful retry so the parent can re-fetch fresh data. */
  onRetried?: () => void;
}

const PullDiagnosticsDrawer: React.FC<Props> = ({ open, onClose, campaign, statuses, onRetried }) => {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retrySuccess, setRetrySuccess] = useState<string | null>(null);

  // Drawer is mounted unconditionally by the parent — clear stale banners
  // each time it (re)opens so a previous run's success/error doesn't linger.
  useEffect(() => {
    if (open) {
      setRetryError(null);
      setRetrySuccess(null);
    }
  }, [open]);

  const handleRetry = async (): Promise<void> => {
    setRetrying(true);
    setRetryError(null);
    setRetrySuccess(null);
    try {
      await refreshCampaign(campaign.canonicalCampaignId);
      setRetrySuccess('Pull triggered. Refresh the dashboard in a few seconds.');
      onRetried?.();
    } catch (err: any) {
      setRetryError(err.message ?? 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="CG Boost"
      title="Pull diagnostics"
      width={640}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 12.5, color: 'var(--fg-3)' }}>
            {retrySuccess && <span style={{ color: 'var(--success)' }}>{retrySuccess}</span>}
            {retryError && <span style={{ color: 'var(--danger)' }}>{retryError}</span>}
          </div>
          <Button variant="ghost" size="md" onClick={onClose} disabled={retrying}>
            Close
          </Button>
          <Button variant="primary" size="md" onClick={handleRetry} disabled={retrying}>
            {retrying ? 'Retrying…' : 'Retry pull (all platforms)'}
          </Button>
        </>
      }
    >
      <div style={body}>
        <div style={subtle}>
          Each platform pulls independently — a single failure here doesn't
          block the others. The retry below re-runs the daily pull for every
          platform that has a registered link.
        </div>

        {statuses.map((s) => (
          <PlatformRow key={s.platform} campaign={campaign} status={s} />
        ))}
      </div>
    </Modal>
  );
};

const PlatformRow: React.FC<{ campaign: CgBoostCampaign; status: CgBoostPullStatus }> = ({
  campaign,
  status,
}) => {
  const ids = resolvePlatformIds(campaign, status.platform);
  return (
    <div style={row}>
      <div style={rowHeader}>
        <PlatformHealthBadge platform={status.platform} status={status} />
        <div style={{ flex: 1 }} />
        <span style={rowTime}>
          {status.lastSuccessAt
            ? `Last success ${formatTimeAgo(status.lastSuccessAt)}`
            : 'Never succeeded'}
        </span>
      </div>

      <dl style={dl}>
        <Row label="Rows last pull" value={String(status.rowsLastPull)} />
        <Row label="Last attempt" value={status.lastAttemptAt ? formatTimeAgo(status.lastAttemptAt) : '—'} />
        <Row label="Last success" value={status.lastSuccessAt ? formatTimeAgo(status.lastSuccessAt) : '—'} />
        {status.lastError && (
          <Row
            label="Last error"
            value={status.lastError}
            mono
            danger
          />
        )}
      </dl>

      {ids ? (
        <details style={details}>
          <summary style={summary}>Resolved IDs</summary>
          <pre style={pre}>{JSON.stringify(ids, null, 2)}</pre>
        </details>
      ) : (
        <div style={muted}>
          No {PLATFORM_LABELS[status.platform]} platform IDs registered for this
          campaign. Add them via PATCH /api/cg-boost/campaigns/:id (linking
          wizard coming soon).
        </div>
      )}
    </div>
  );
};

function resolvePlatformIds(campaign: CgBoostCampaign, platform: CgBoostPlatform): unknown {
  return campaign.platformIds?.[platform] ?? null;
}

const Row: React.FC<{ label: string; value: string; mono?: boolean; danger?: boolean }> = ({
  label,
  value,
  mono,
  danger,
}) => (
  <>
    <dt style={dt}>{label}</dt>
    <dd
      style={{
        ...dd,
        fontFamily: mono ? 'var(--font-mono)' : undefined,
        color: danger ? 'var(--danger)' : undefined,
        whiteSpace: mono ? 'pre-wrap' : 'normal',
        wordBreak: mono ? 'break-word' : 'normal',
      }}
    >
      {value}
    </dd>
  </>
);

const body: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };
const subtle: React.CSSProperties = { fontSize: 12.5, color: 'var(--fg-3)', lineHeight: 1.5 };

const row: React.CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 14,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  background: '#fff',
};
const rowHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10 };
const rowTime: React.CSSProperties = { fontSize: 12, color: 'var(--fg-3)' };

const dl: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '140px 1fr',
  rowGap: 6,
  columnGap: 12,
  margin: 0,
  fontSize: 12.5,
};
const dt: React.CSSProperties = { color: 'var(--fg-3)', fontWeight: 600 };
const dd: React.CSSProperties = { margin: 0, color: 'var(--fg-1)' };

const details: React.CSSProperties = { fontSize: 12 };
const summary: React.CSSProperties = { cursor: 'pointer', color: 'var(--fg-3)', fontWeight: 600 };
const pre: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11.5,
  background: 'var(--bg-hover)',
  padding: 10,
  borderRadius: 'var(--radius-sm)',
  marginTop: 8,
  overflowX: 'auto',
};
const muted: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--fg-3)',
  fontStyle: 'italic',
};

export default PullDiagnosticsDrawer;
